import { keyframes } from '@emotion/react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, CircularProgress, Typography } from '@mui/material';
import { STUDENT_BLUE as BLUE, STUDENT_GREEN as GREEN, STUDENT_NAVY as NAVY, STUDENT_POPPINS as POPPINS, STUDENT_RING as RING } from '../style/verbaScholaStudentStyles';

const checkPop = keyframes`
    from { transform: scale(0.6); opacity: 0.4; }
    to { transform: scale(1); opacity: 1; }
`;

/**
 * One ring for progress and for pronunciation scores. Progress turns into the green completion
 * check only when the work is actually complete: rounding never completes it (style guide), so an
 * unfinished 99.6% shows 99%. A score is a measurement, never a completion: it always shows its number.
 */
export default function VerbaScholaProgressRing({ percent, size = 32, thickness = 5, showLabel = false, ariaLabel, variant = 'progress', complete }: {
    percent: number | null;
    size?: number;
    thickness?: number;
    showLabel?: boolean;
    ariaLabel?: string;
    variant?: 'progress' | 'score';
    /** Progress only: completion from counts. Defaults to the unrounded percent reaching 100. */
    complete?: boolean;
}) {
    const raw = percent === null || !Number.isFinite(percent) ? null : Math.max(0, Math.min(100, percent));
    if (raw === null) {
        return <Box role="img" aria-label={variant === 'score' ? 'Score unavailable' : 'Progress unavailable'} sx={{ width: size, height: size, borderRadius: '50%', border: `${thickness}px solid ${RING}`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            {showLabel && <Typography aria-hidden="true" sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: Math.max(11, size * 0.22) }}>–</Typography>}
        </Box>;
    }
    const finished = variant === 'progress' && (complete ?? raw >= 100);
    if (finished) {
        return <Box role="img" aria-label={ariaLabel ?? '100% complete'} sx={{ width: size, height: size, borderRadius: '50%', backgroundColor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckRoundedIcon sx={{ color: '#fff', fontSize: size * 0.6, animation: `${checkPop} 250ms ease-out`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
        </Box>;
    }
    const shown = variant === 'progress' ? Math.min(99, Math.round(raw)) : Math.round(raw);
    return <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
        <CircularProgress aria-hidden variant="determinate" value={100} size={size} thickness={thickness} sx={{ color: RING, position: 'absolute', left: 0 }} />
        <CircularProgress aria-label={ariaLabel ?? (variant === 'score' ? `Score ${shown} percent` : `${shown}% complete`)} variant="determinate" value={variant === 'score' ? raw : shown} size={size} thickness={thickness}
            sx={{ color: BLUE, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
        {showLabel && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: Math.max(11, size * 0.22) }}>{shown}%</Typography>
        </Box>}
    </Box>;
}
